import React, { useEffect, useState } from 'react';
import { X, FileText, Edit2, Download } from 'lucide-react';

const ContenedorDetailPanel = ({
  isOpen,
  contenedor,
  onClose,
  onEdit,
  onTimeline,
  puertos
}) => {
  const [logoBase64, setLogoBase64] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Cargar logo
  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const response = await fetch('/LogoDragon.png');
        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoBase64(reader.result);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.log('Logo no encontrado');
      }
    };
    cargarLogo();
  }, []);

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevenir scroll del body cuando panel está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!contenedor) return null;

  // Helper para obtener código de puerto
  const getCodigoPuerto = (nombrePuerto) => {
    if (!nombrePuerto) return '-';
    const puerto = puertos?.find(p => p.nombre === nombrePuerto);
    return puerto?.codigo || nombrePuerto;
  };

  // Helper para color de estado
  const getEstadoColor = (estado) => {
    const estadosConfig = {
      'En Tránsito': 'bg-blue-100 text-blue-800',
      'En Almacén': 'bg-yellow-100 text-yellow-800',
      'Entregado': 'bg-green-100 text-green-800',
      'Retirado': 'bg-purple-100 text-purple-800',
      'Vacío': 'bg-gray-100 text-gray-800',
    };
    return estadosConfig[estado] || 'bg-gray-100 text-gray-800';
  };

  // Calcular días a facturar en tiempo real:
  // prioridad: hastaQueFecha > fechaVacio > hoy
  const calcularDiasAFacturar = () => {
    if (!contenedor.ultimaFactura) return parseFloat(contenedor.diasAFacturar) || 0;

    const parseDate = (str) => {
      if (!str) return null;
      // YYYY-MM-DD
      const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
      // DD/MM/YYYY
      const dmy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dmy) return new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
      return null;
    };

    const inicio = parseDate(contenedor.ultimaFactura);
    if (!inicio) return parseFloat(contenedor.diasAFacturar) || 0;

    const endStr = contenedor.hastaQueFecha || contenedor.fechaVacio || null;
    const fin = endStr ? parseDate(endStr) : new Date();
    if (!fin) return parseFloat(contenedor.diasAFacturar) || 0;

    inicio.setHours(0, 0, 0, 0);
    fin.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((fin.getTime() - inicio.getTime()) / 86400000);
    return diffDays >= 0 ? diffDays + 1 : 0;
  };

  // Calcular utilidad
  const costoConIVA = parseFloat(contenedor.costoNavieraConIVA) || 0;
  const ventaConIVA = parseFloat(contenedor.ventaClienteConIVA) || 0;
  const utilidad = ventaConIVA - costoConIVA;
  const utilidadPositiva = utilidad >= 0;

  const diasAFacturar = calcularDiasAFacturar();

  // Usar valores guardados; si son 0, derivarlos de ventaClienteSinIVA / diasDemoras × diasAFacturar
  const ventaFacturacionSinIVA = (() => {
    const fromDB = parseFloat(contenedor.ventaFacturacionSinIVA);
    if (fromDB > 0) return fromDB;
    const diasDemorasDB = parseFloat(contenedor.diasDemoras);
    const ventaBase = parseFloat(contenedor.ventaClienteSinIVA);
    if (diasDemorasDB > 0 && ventaBase > 0 && diasAFacturar > 0) {
      return (ventaBase / diasDemorasDB) * diasAFacturar;
    }
    return 0;
  })();

  const ventaFacturacionConIVA = (() => {
    const fromDB = parseFloat(contenedor.ventaFacturacionConIVA);
    if (fromDB > 0) return fromDB;
    if (ventaFacturacionSinIVA > 0) {
      return contenedor.aplicaIVA !== false
        ? ventaFacturacionSinIVA * 1.16
        : ventaFacturacionSinIVA;
    }
    return 0;
  })();

  // Label de la fecha final usada para facturar
  const fechaFinFacturacion = contenedor.hastaQueFecha || contenedor.fechaVacio || null;

  // Función para exportar PDF — layout compacto, todo en una sola hoja A4
  const exportarPDF = async () => {
    if (!contenedor) return;

    setGenerandoPDF(true);

    try {
      const jsPDF = (await import('jspdf')).default;

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth  = doc.internal.pageSize.width;   // 210 mm
      const pageHeight = doc.internal.pageSize.height;  // 297 mm

      const fechaActual = new Date().toLocaleDateString('es-MX', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
      const horaActual = new Date().toLocaleTimeString('es-MX', {
        hour: '2-digit', minute: '2-digit'
      });

      // ========== HEADER ==========
      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', 15, 7, 30, 13);
        } catch (e) { /* logo opcional */ }
      }

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 41, 55);
      doc.text('DETALLE DE CONTENEDOR', pageWidth / 2, 14, { align: 'center' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(`${fechaActual}  ·  ${horaActual}`, pageWidth / 2, 21, { align: 'center' });

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(15, 25, pageWidth - 15, 25);

      let yPos = 31;

      // ========== CONTENEDOR Y CLIENTE ==========
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(15, yPos, pageWidth - 30, 14, 2, 2, 'F');

      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text('Contenedor:', 20, yPos + 6);
      doc.setFontSize(13);
      doc.setTextColor(37, 99, 235);
      doc.text(contenedor.contenedor || '-', 47, yPos + 6);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text('Cliente:', 20, yPos + 11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text(contenedor.cliente || '-', 34, yPos + 11);

      yPos += 19;

      // ========== INFORMACIÓN OPERATIVA ==========
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text('INFORMACIÓN OPERATIVA', 15, yPos);

      yPos += 2;
      doc.setDrawColor(229, 231, 235);
      doc.line(15, yPos, pageWidth - 15, yPos);
      yPos += 5;

      doc.setFillColor(249, 250, 251);
      doc.roundedRect(15, yPos - 3, pageWidth - 30, 30, 2, 2, 'F');

      const infoOperativa = [
        { label: 'Folio:',                  value: contenedor.folio || '-' },
        { label: 'Master:',                 value: contenedor.master || '-' },
        { label: 'POD:',                    value: getCodigoPuerto(contenedor.pod) },
        { label: 'ATA:',                    value: contenedor.ata || '-' },
        { label: 'Último Día Sin Demoras:', value: contenedor.ultimoDiaSinDemoras || '-' },
        { label: 'Fecha de Vacío:',         value: contenedor.fechaVacio || '-' },
        { label: 'Estado:',                 value: contenedor.estadoActual || '-' },
      ];

      doc.setFontSize(8.5);
      infoOperativa.forEach((item, index) => {
        const row  = Math.floor(index / 2);
        const col  = index % 2;
        const xPos = col === 0 ? 20 : 112;
        const yOff = yPos + row * 5.2;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text(item.label, xPos, yOff);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(31, 41, 55);
        doc.text(item.value, xPos + doc.getTextWidth(item.label) + 1.5, yOff);
      });

      yPos += 34;

      // ========== DESGLOSE FINANCIERO ==========
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text('DESGLOSE FINANCIERO', 15, yPos);

      yPos += 2;
      doc.setDrawColor(229, 231, 235);
      doc.line(15, yPos, pageWidth - 15, yPos);
      yPos += 5;

      const ivaLabel = contenedor.aplicaIVA !== false ? 'Con IVA:' : 'Total (s/IVA):';

      // Card Costo Naviera
      doc.setFillColor(254, 242, 242);
      doc.setDrawColor(254, 202, 202);
      doc.setLineWidth(0.5);
      doc.roundedRect(15, yPos, 85, 20, 2, 2, 'FD');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(127, 29, 29);
      doc.text('COSTO NAVIERA', 20, yPos + 6);

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(185, 28, 28);
      doc.text('Base s/IVA:', 20, yPos + 12);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${(parseFloat(contenedor.costoNavieraSinIVA) || 0).toFixed(2)}`, 40, yPos + 12);

      doc.setFont('helvetica', 'normal');
      doc.text(ivaLabel, 20, yPos + 17);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${(parseFloat(contenedor.costoNavieraConIVA) || 0).toFixed(2)}`, 40, yPos + 17);

      // Card Venta Cliente
      doc.setFillColor(236, 253, 245);
      doc.setDrawColor(167, 243, 208);
      doc.roundedRect(110, yPos, 85, 20, 2, 2, 'FD');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(6, 78, 59);
      doc.text('VENTA CLIENTE', 115, yPos + 6);

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(5, 150, 105);
      doc.text('Base s/IVA:', 115, yPos + 12);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${(parseFloat(contenedor.ventaClienteSinIVA) || 0).toFixed(2)}`, 135, yPos + 12);

      doc.setFont('helvetica', 'normal');
      doc.text(ivaLabel, 115, yPos + 17);
      doc.setFont('helvetica', 'bold');
      doc.text(`$${(parseFloat(contenedor.ventaClienteConIVA) || 0).toFixed(2)}`, 135, yPos + 17);

      yPos += 24;

      // Barra de Utilidad
      const utilidadColor  = utilidadPositiva ? [236, 253, 245] : [254, 242, 242];
      const utilidadBorder = utilidadPositiva ? [167, 243, 208] : [254, 202, 202];
      const utilidadText   = utilidadPositiva ? [6, 78, 59]     : [127, 29, 29];

      doc.setFillColor(...utilidadColor);
      doc.setDrawColor(...utilidadBorder);
      doc.roundedRect(15, yPos, pageWidth - 30, 9, 2, 2, 'FD');

      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...utilidadText);
      doc.text(`Utilidad: $${utilidad.toFixed(2)}`, pageWidth / 2, yPos + 6, { align: 'center' });

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      const ivaAplicadoTexto = contenedor.aplicaIVA !== false ? 'IVA Aplicado: SÍ (16%)' : 'IVA Aplicado: NO';
      doc.text(ivaAplicadoTexto, pageWidth - 17, yPos + 6, { align: 'right' });

      yPos += 14;

      // ========== FACTURACIÓN ==========
      if (contenedor.ultimaFactura) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(107, 114, 128);
        doc.text('FACTURACIÓN', 15, yPos);

        yPos += 2;
        doc.setDrawColor(229, 231, 235);
        doc.line(15, yPos, pageWidth - 15, yPos);
        yPos += 5;

        const pagadoLabel = contenedor.pagado === 'CLIENTE'
          ? 'PAGADO POR EL CLIENTE'
          : contenedor.pagado === 'EMPRESA'
          ? 'ABSORBIDO POR DRAGON CARGO'
          : 'PENDIENTE';

        doc.setFillColor(239, 246, 255);
        doc.roundedRect(15, yPos - 3, pageWidth - 30, 27, 2, 2, 'F');

        const fechaHasta = contenedor.hastaQueFecha || contenedor.fechaVacio || 'Hoy';
        const infoFacturacion = [
          { label: 'Última Factura:',  value: contenedor.ultimaFactura || '-' },
          { label: 'Hasta:',           value: fechaHasta },
          { label: 'Días a Facturar:', value: String(diasAFacturar) },
          { label: 'Estado Factura:',  value: contenedor.factura === 'SI' ? 'SI' : 'NO' },
          { label: 'Estado de Pago:',  value: pagadoLabel },
        ];

        doc.setFontSize(8.5);
        infoFacturacion.forEach((item, index) => {
          const row  = Math.floor(index / 2);
          const col  = index % 2;
          const xPos = col === 0 ? 20 : 112;
          const yOff = yPos + row * 5.2;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(107, 114, 128);
          doc.text(item.label, xPos, yOff);

          doc.setFont('helvetica', 'bold');
          doc.setTextColor(31, 41, 55);
          doc.text(String(item.value), xPos + doc.getTextWidth(item.label) + 1.5, yOff);
        });

        yPos += 31;

        // ========== FACTURA Y PAGO A NAVIERA ==========
        if (contenedor.facturaPagoNaviera) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(107, 114, 128);
          doc.text('FACTURA Y PAGO A NAVIERA', 15, yPos);

          yPos += 2;
          doc.setDrawColor(229, 231, 235);
          doc.line(15, yPos, pageWidth - 15, yPos);
          yPos += 4;

          doc.setFontSize(8.5);
          const lineas      = doc.splitTextToSize(contenedor.facturaPagoNaviera, pageWidth - 46);
          const alturaTexto = lineas.length * 4.5 + 7;

          doc.setFillColor(255, 251, 235);
          doc.setDrawColor(252, 211, 77);
          doc.setLineWidth(0.5);
          doc.roundedRect(15, yPos - 2, pageWidth - 30, alturaTexto, 2, 2, 'FD');

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(55, 65, 81);
          doc.text(lineas, 20, yPos + 3);
          yPos += alturaTexto + 5;
        }

        // ========== VENTAS POR DEMORAS PARCIALES ==========
        if (diasAFacturar > 0 && ventaFacturacionSinIVA > 0) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(107, 114, 128);
          doc.text('VENTAS POR DEMORAS PARCIALES', 15, yPos);

          yPos += 2;
          doc.setDrawColor(229, 231, 235);
          doc.line(15, yPos, pageWidth - 15, yPos);
          yPos += 4;

          doc.setFillColor(250, 245, 255);
          doc.setDrawColor(216, 180, 254);
          doc.setLineWidth(0.8);
          doc.roundedRect(15, yPos - 2, pageWidth - 30, 25, 2, 2, 'FD');

          // Mini-card SIN IVA
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(216, 180, 254);
          doc.setLineWidth(0.3);
          doc.roundedRect(20, yPos + 1, 80, 18, 2, 2, 'FD');

          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(107, 114, 128);
          doc.text('Venta Cliente SIN IVA', 25, yPos + 6);

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(147, 51, 234);
          doc.text(`$${ventaFacturacionSinIVA.toFixed(2)}`, 25, yPos + 12);

          doc.setFontSize(6.5);
          doc.text(`${diasAFacturar} días facturados`, 25, yPos + 16.5);

          // Mini-card CON IVA
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(110, yPos + 1, 80, 18, 2, 2, 'FD');

          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(107, 114, 128);
          doc.text('Venta Cliente CON IVA', 115, yPos + 6);

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(126, 34, 206);
          doc.text(`$${ventaFacturacionConIVA.toFixed(2)}`, 115, yPos + 12);

          doc.setFontSize(6.5);
          doc.setTextColor(147, 51, 234);
          doc.text(`$${ventaFacturacionSinIVA.toFixed(2)} × 1.16`, 115, yPos + 16.5);
        }
      }

      // ========== FOOTER ==========
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(15, pageHeight - 14, pageWidth - 15, pageHeight - 14);

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text('Dragon Cargo - Sistema de Gestión de Contenedores', 15, pageHeight - 7);
      doc.text(`Generado: ${fechaActual}  ${horaActual}`, pageWidth - 15, pageHeight - 7, { align: 'right' });

      const nombreArchivo = `Contenedor_${contenedor.contenedor || 'detalle'}_${Date.now()}.pdf`;
      doc.save(nombreArchivo);

    } catch (error) {
      console.error('❌ Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor intenta de nuevo.');
    } finally {
      setGenerandoPDF(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {contenedor.contenedor || '-'}
              </h2>
              <p className="text-gray-600 mt-1">
                Cliente: {contenedor.cliente || '-'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Cerrar panel"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content (scrollable) */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Sección 1: Info Operativa */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                Información Operativa
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Folio:</span>
                  <span className="font-medium">{contenedor.folio || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Master:</span>
                  <span className="font-medium">{contenedor.master || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">POD:</span>
                  <span className="font-medium" title={contenedor.pod}>
                    {getCodigoPuerto(contenedor.pod)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ATA:</span>
                  <span className="font-medium">{contenedor.ata || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Último Día Sin Demoras:</span>
                  <span className="font-medium">{contenedor.ultimoDiaSinDemoras || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Fecha de Vacío:</span>
                  <span className="font-medium">{contenedor.fechaVacio || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Estado:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoColor(contenedor.estadoActual)}`}>
                    {contenedor.estadoActual || '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sección 2: Financiero */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase">
                  Desglose Financiero
                </h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  contenedor.aplicaIVA !== false
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {contenedor.aplicaIVA !== false ? 'CON IVA 16%' : 'SIN IVA'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-red-50/50 backdrop-blur-xl rounded-lg p-4 border border-red-100">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">💰</span>
                    <h4 className="font-semibold text-red-900">Costo Naviera</h4>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-red-700">Base (s/IVA):</span>
                      <span className="font-bold text-red-900">
                        ${(parseFloat(contenedor.costoNavieraSinIVA) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-700">
                        {contenedor.aplicaIVA !== false ? 'Con IVA (16%):' : 'Total (s/IVA):'}
                      </span>
                      <span className="font-bold text-red-900">
                        ${(parseFloat(contenedor.costoNavieraConIVA) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50/50 backdrop-blur-xl rounded-lg p-4 border border-green-100">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">💵</span>
                    <h4 className="font-semibold text-green-900">Venta Cliente</h4>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-700">Base (s/IVA):</span>
                      <span className="font-bold text-green-900">
                        ${(parseFloat(contenedor.ventaClienteSinIVA) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">
                        {contenedor.aplicaIVA !== false ? 'Con IVA (16%):' : 'Total (s/IVA):'}
                      </span>
                      <span className="font-bold text-green-900">
                        ${(parseFloat(contenedor.ventaClienteConIVA) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`mt-4 p-4 rounded-lg text-center ${
                utilidadPositiva
                  ? 'bg-green-100 border border-green-200'
                  : 'bg-red-100 border border-red-200'
              }`}>
                <span className={`text-lg font-bold ${
                  utilidadPositiva ? 'text-green-800' : 'text-red-800'
                }`}>
                  {utilidadPositiva ? '✅' : '⚠️'} Utilidad: ${utilidad.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Sección 3: Facturación (siempre visible si hay ultimaFactura) */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                Facturación
              </h3>
              <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Última Factura:</span>
                  <span className="font-medium">{contenedor.ultimaFactura || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Días a Facturar:</span>
                  <span className="px-2 py-1 bg-blue-200 text-blue-900 rounded-full font-semibold">
                    {diasAFacturar}
                  </span>
                </div>
                {fechaFinFacturacion && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {contenedor.hastaQueFecha ? 'Hasta qué Fecha:' : 'Hasta (Fecha de Vacío):'}
                    </span>
                    <span className="font-medium">{fechaFinFacturacion}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Estado Factura:</span>
                  <span className={`px-3 py-1 rounded-full font-semibold ${
                    contenedor.factura === 'SI'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {contenedor.factura === 'SI' ? '✅ SI' : '❌ NO'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Estado de Pago:</span>
                  <span className={`px-3 py-1 rounded-full font-semibold text-xs ${
                    contenedor.pagado === 'CLIENTE'
                      ? 'bg-green-100 text-green-800'
                      : contenedor.pagado === 'EMPRESA'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {contenedor.pagado === 'CLIENTE'
                      ? '✅ PAGADO POR EL CLIENTE'
                      : contenedor.pagado === 'EMPRESA'
                      ? '🔵 ABSORBIDO POR DRAGON CARGO'
                      : '⏳ PENDIENTE'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sección: Factura y Pago a Naviera */}
            {contenedor.facturaPagoNaviera && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  Factura y Pago a Naviera
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                    {contenedor.facturaPagoNaviera}
                  </p>
                </div>
              </div>
            )}

            {/* Sección 4: Ventas por Demoras Parciales */}
            {diasAFacturar > 0 && contenedor.factura === 'SI' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  Ventas por Demoras Parciales
                </h3>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-4">
                  {ventaFacturacionSinIVA > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-purple-200">
                          <p className="text-xs text-gray-600 mb-1 font-medium">
                            Venta Cliente SIN IVA
                          </p>
                          <p className="text-xl font-bold text-purple-600">
                            ${ventaFacturacionSinIVA.toFixed(2)}
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            {diasAFacturar} días facturados
                          </p>
                        </div>

                        <div className="bg-white rounded-lg p-3 shadow-sm border border-purple-200">
                          <p className="text-xs text-gray-600 mb-1 font-medium">
                            Venta Cliente CON IVA
                          </p>
                          <p className="text-xl font-bold text-purple-700">
                            ${ventaFacturacionConIVA.toFixed(2)}
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            ${ventaFacturacionSinIVA.toFixed(2)} × 1.16
                          </p>
                        </div>
                      </div>

                      <div className="bg-purple-100 rounded-lg p-2 border border-purple-300">
                        <p className="text-xs text-purple-800 font-semibold">
                          💡 Venta basada en <span className="text-purple-900">{diasAFacturar} días facturados</span>
                          {contenedor.ultimaFactura && ` desde ${contenedor.ultimaFactura}`}
                          {fechaFinFacturacion && ` hasta ${fechaFinFacturacion}`}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-300">
                      <p className="text-sm text-yellow-800 font-medium text-center">
                        ⚠️ Este registro tiene {diasAFacturar} días a facturar pero no tiene valores de venta calculados.
                      </p>
                      <p className="text-xs text-yellow-700 text-center mt-2">
                        Haz clic en <strong>"Editar"</strong> y guarda el registro para calcular los valores automáticamente.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t p-6 flex gap-3">
            <button
              onClick={exportarPDF}
              disabled={generandoPDF}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download size={20} />
              <span>{generandoPDF ? 'Generando...' : 'Exportar PDF'}</span>
            </button>
            <button
              onClick={() => onTimeline(contenedor)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <FileText size={20} />
              <span>Ver Timeline</span>
            </button>
            <button
              onClick={() => onEdit(contenedor)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Edit2 size={20} />
              <span>Editar</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContenedorDetailPanel;
